/**
 * Checklist editor for a provider's offered appointment types.
 *
 * Shared by the My Settings "Appointment Types I Offer" section and the
 * OfferedAppointmentTypesModal launched from appointment-type selects.
 *
 * When `practitionerId` matches the logged-in user's practitioner (or is
 * omitted), the self-service endpoints are used; otherwise the admin
 * provider endpoints are used (requires `settings.users` permission).
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../../contexts/AuthContext';
import { post, put } from '../../utils/apiClient';
import { useApiGet, useQueryClient } from '../../hooks/useApiQuery';
import { logger } from '../../utils/logger';
import { toast } from 'react-toastify';

type OfferedAppointmentType = {
  appointment_type_id: string;
  enabled: boolean;
  category?: string | null;
  name: string;
  duration_minutes: number;
};

interface OfferedAppointmentTypesEditorProps {
  /** Practitioner to edit; defaults to the logged-in user's practitioner. */
  practitionerId?: string | null;
  /** Called after a successful save so callers can refresh dependent data. */
  onSaved?: () => void;
}

export default function OfferedAppointmentTypesEditor({
  practitionerId,
  onSaved,
}: OfferedAppointmentTypesEditorProps) {
  const { user } = useAuth();
  // Narrow user from AuthUser | false | null to AuthUser | null for safe property access
  const authUser = user && typeof user === 'object' ? user : null;
  const selfPractitionerId = (authUser as { practitioner_id?: string })?.practitioner_id;
  const isSelf = !practitionerId || practitionerId === selfPractitionerId;
  const targetPractitionerId = isSelf ? authUser?.uuid : practitionerId;
  const basePath = isSelf
    ? `/api/auth/practitioners/${targetPractitionerId}/offered-appointment-types`
    : `/api/admin/providers/${practitionerId}/offered-appointment-types`;

  const [offeredTypes, setOfferedTypes] = useState<OfferedAppointmentType[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-channel self-scheduling (Client Portal / Booking Widget) moved to the
  // canonical provider drill-down under Settings → Booking & Intake. This
  // editor now only controls which types the provider offers.

  const { data: offeredData, isLoading: loading } = useApiGet(
    targetPractitionerId
      ? [isSelf ? 'practitioners' : 'admin-providers', targetPractitionerId, 'offered-appointment-types']
      : null,
    basePath,
  );

  useEffect(() => {
    if (offeredData) {
      const appointmentTypes = Array.isArray(offeredData.appointment_types) ? offeredData.appointment_types : [];
      setOfferedTypes(appointmentTypes as OfferedAppointmentType[]);
    }
  }, [offeredData]);

  const handleToggleOfferedType = (appointmentTypeId: string, enabled: boolean) => {
    setOfferedTypes(prev => prev.map(apt => {
      if (apt.appointment_type_id === appointmentTypeId) {
        return { ...apt, enabled };
      }
      return apt;
    }));
  };

  const handleSelectAllCategory = (category: string, selected: boolean) => {
    setOfferedTypes(prev => prev.map(apt => {
      if (apt.category === category) {
        return { ...apt, enabled: selected };
      }
      return apt;
    }));
  };

  const queryClient = useQueryClient();

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const enabledTypeIds = offeredTypes
        .filter(apt => apt.enabled)
        .map(apt => apt.appointment_type_id);

      if (isSelf) {
        await post(basePath, { appointment_type_ids: enabledTypeIds });
      } else {
        await put(basePath, { appointment_type_ids: enabledTypeIds });
      }

      queryClient.invalidateQueries({
        queryKey: [isSelf ? 'practitioners' : 'admin-providers', targetPractitionerId, 'offered-appointment-types'],
      });

      toast.success('Appointment types saved');
      onSaved?.();
    } catch (err) {
      logger.error('Error saving offered appointment types:', err);
      setError('Failed to save appointment types');
    } finally {
      setSaving(false);
    }
  };

  const groupedOfferedTypes = offeredTypes.reduce<Record<string, OfferedAppointmentType[]>>((acc, apt) => {
    const category = apt.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(apt);
    return acc;
  }, {});

  const isCategoryAllSelected = (category: string) => {
    const categoryTypes = groupedOfferedTypes[category] || [];
    return categoryTypes.length > 0 && categoryTypes.every(apt => apt.enabled);
  };

  const isCategorySomeSelected = (category: string) => {
    const categoryTypes = groupedOfferedTypes[category] || [];
    return categoryTypes.some(apt => apt.enabled) && !isCategoryAllSelected(category);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {Object.keys(groupedOfferedTypes).length === 0 ? (
        <Alert severity="info">
          No appointment types have been created for your organization yet.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Pick the appointment types this provider offers. Per-channel
            self-scheduling (Client Portal / Booking Widget) is managed by an
            admin under <strong>Settings → Booking &amp; Intake</strong>.
          </Typography>
          {Object.entries(groupedOfferedTypes).map(([category, types]) => (
            <Accordion key={category} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <FormControlLabel
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  control={
                    <Checkbox
                      checked={isCategoryAllSelected(category)}
                      indeterminate={isCategorySomeSelected(category)}
                      onChange={(e) => handleSelectAllCategory(category, e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {category} ({types.filter(t => t.enabled).length}/{types.length})
                    </Typography>
                  }
                />
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  {types.map((apt) => (
                    <Box key={apt.appointment_type_id} sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        sx={{ flex: 1 }}
                        control={
                          <Checkbox
                            checked={apt.enabled}
                            onChange={(e) => handleToggleOfferedType(apt.appointment_type_id, e.target.checked)}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">{apt.name}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {apt.duration_minutes} min
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          ))}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              size="small"
            >
              {saving ? 'Saving...' : 'Save Appointment Types'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
